/**
 * Lire ce que Supabase refuse, et savoir POURQUOI.
 *
 * Le code est déployé automatiquement toutes les cinq minutes ; les migrations
 * se passent à la main. Entre les deux, l'application tourne sur une base qui
 * n'a pas encore ses nouvelles colonnes, et elle doit continuer à fonctionner —
 * sans colonne, mais sans mentir.
 *
 * Tout tient alors à distinguer deux refus qui se ressemblent et qui n'appellent
 * pas du tout la même conduite :
 *
 *  - **la TABLE manque** : la migration qui la crée n'est pas passée, il n'y a
 *    rien à lire, une liste vide est la vérité ;
 *  - **une COLONNE manque** : la table est là, pleine de données, et il faut
 *    relire sans la colonne. Rendre une liste vide serait un mensonge — et c'est
 *    exactement ce qui s'est produit le 06/10/2026 : 55 règles de rapprochement
 *    ont disparu de l'écran, « Aucune règle pour le moment », alors qu'elles
 *    étaient toutes en base. La comparaison au relevé s'est tue avec elles.
 *
 * Elles se confondent parce que PostgREST écrit les deux de quatre façons, dont
 * deux citent le nom de la table :
 *
 * | Ce qui manque | À la lecture | À l'écriture |
 * | --- | --- | --- |
 * | la table | `relation "public.t" does not exist` | `Could not find the table 'public.t' in the schema cache` |
 * | une colonne | `column t.c does not exist` | `Could not find the 'c' column of 't' in the schema cache` |
 *
 * Un test naïf sur « le nom de la table, puis *does not exist* » reconnaît la
 * ligne « colonne » autant que la ligne « table ». D'où ces deux fonctions, et
 * d'où leurs tests : une erreur mal rangée se paie en chiffres absents, pas en
 * écran cassé, et personne ne le voit.
 */

/** La colonne demandée n'existe pas (encore) sur une table qui, elle, existe. */
export function colonneAbsente(message: string): boolean {
  return (
    /\bcolumn\b[^]*\bdoes not exist\b/i.test(message) ||
    /could not find the '[^']+' column\b/i.test(message)
  );
}

/**
 * La table elle-même n'existe pas.
 *
 * `table` est le nom attendu : sans lui, l'absence d'une AUTRE table passerait
 * pour celle qu'on cherche. Une colonne manquante n'est jamais une table
 * manquante, quoi que dise le message.
 */
export function tableAbsente(message: string, table: string): boolean {
  if (colonneAbsente(message)) return false;
  const nom = table.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return (
    new RegExp(String.raw`relation\b[^]*\b${nom}\b[^]*does not exist`, 'i').test(message) ||
    new RegExp(String.raw`could not find the table\b[^]*\b${nom}\b`, 'i').test(message)
  );
}

/**
 * Ce qu'il faut faire d'une écriture refusée, quand la base peut ne pas avoir
 * encore reçu ses nouvelles colonnes.
 */
export type SuiteApresRefus =
  /** Le refus n'a rien à voir avec les colonnes : il remonte tel quel. */
  | 'propager'
  /** Les colonnes manquent, mais rien ne s'en servait : réécrire sans elles. */
  | 'reessayer-sans'
  /** Les colonnes manquent ET portaient une valeur : refuser, et dire pourquoi. */
  | 'refuser';

/**
 * Une écriture vient d'être refusée. On continue, ou on s'arrête ?
 *
 * `colonnesRenseignees` dit si les colonnes récentes portaient une valeur.
 * C'est tout l'enjeu : réécrire sans elles est juste quand elles étaient vides
 * — l'enregistrement est alors exactement celui d'avant la migration — et faux
 * quand elles portaient quelque chose, car la condition saisie disparaîtrait en
 * silence et la règle ramasserait bien plus que ce qu'on lui demandait.
 *
 * Le 06/10/2026, faute de cette distinction, PLUS AUCUNE règle n'était créable :
 * envoyer `montant_min: null` à une base qui ignore la colonne suffisait à tout
 * faire refuser, y compris les règles qui ne s'en servaient pas.
 */
export function suiteApresRefus(message: string, colonnesRenseignees: boolean): SuiteApresRefus {
  if (!colonneAbsente(message)) return 'propager';
  return colonnesRenseignees ? 'refuser' : 'reessayer-sans';
}
